<template>
  <canvas ref="canvasRef" class="melon-rain" aria-hidden="true"></canvas>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, watch } from "vue";
import { useQuotesStore } from "@/store/quotes";

// Cuántas citas "representa" cada trozo, y límites para que la
// cantidad de trozos no sea ridícula (muy pocas citas) ni un exceso
// visual (muchísimas citas). Al dibujar en canvas en vez de un
// elemento SVG por trozo, el tope puede ser mucho más alto sin coste
// de rendimiento (nada de nodos DOM, todo se pinta en un solo canvas).
const QUOTES_PER_SLICE = 1;
const MIN_SLICES = 1;
const MAX_SLICES = 2000;
// Mientras no sabemos el total (primera carga), mostramos esta cantidad.
const FALLBACK_SLICES = 0;

const quotesStore = useQuotesStore();

function sliceCountFor(total) {
  if (!total) return FALLBACK_SLICES;
  const target = Math.round(total / QUOTES_PER_SLICE);
  return Math.min(Math.max(target, MIN_SLICES), MAX_SLICES);
}

const canvasRef = ref(null);
let ctx = null;
let width = 0;
let height = 0;
let particles = [];
let rafId = null;
let lastTime = 0;

// Colores leídos de las variables CSS del tema (canvas no entiende
// var(--melon) directamente, hay que resolverlas a un color real).
let melonColor = "#FF7A59";
let rindColor = "#8CA86B";
let seedColor = "#D9A441";

function readThemeColors() {
  const styles = getComputedStyle(document.documentElement);
  melonColor = styles.getPropertyValue("--melon").trim() || melonColor;
  rindColor = styles.getPropertyValue("--rind").trim() || rindColor;
  seedColor = styles.getPropertyValue("--seed").trim() || seedColor;
}

function randomBetween(min, max) {
  return Math.random() * (max - min) + min;
}

function createParticle(resetY) {
  const size = randomBetween(9, 19); // radio del trozo
  return {
    x: randomBetween(0, width),
    y: resetY ? randomBetween(-height, 0) : randomBetween(-height * 2, height),
    size,
    speed: randomBetween(12, 26), // px/segundo
    rotation: randomBetween(0, Math.PI * 2),
    rotationSpeed: randomBetween(-0.6, 0.6), // rad/segundo
  };
}

function setParticleCount(count) {
  if (count > particles.length) {
    const extra = Array.from({ length: count - particles.length }, () =>
      createParticle(false)
    );
    particles.push(...extra);
  } else if (count < particles.length) {
    particles.length = count;
  }
}

// Dibuja un trozo de melón (gajo + borde de corteza + una semilla),
// ya posicionado/rotado por el llamador.
function drawSlice(size) {
  const halfAngle = Math.PI / 6; // ~30°, como el gajo de SliceMark.vue

  ctx.globalAlpha = 0.96;

  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.arc(0, 0, size, -halfAngle, halfAngle);
  ctx.closePath();
  ctx.fillStyle = melonColor;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(0, 0, size, -halfAngle, halfAngle);
  ctx.lineWidth = size * 0.12;
  ctx.strokeStyle = rindColor;
  ctx.lineCap = "round";
  ctx.stroke();

  ctx.beginPath();
  ctx.ellipse(size * 0.55, 0, size * 0.06, size * 0.1, 0, 0, Math.PI * 2);
  ctx.fillStyle = seedColor;
  ctx.fill();

  ctx.globalAlpha = 1;
}

function resizeCanvas() {
  const canvas = canvasRef.value;
  if (!canvas) return;
  width = window.innerWidth;
  height = window.innerHeight;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = width + "px";
  canvas.style.height = height + "px";
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

function tick(time) {
  if (!lastTime) lastTime = time;
  const dt = (time - lastTime) / 1000;
  lastTime = time;

  ctx.clearRect(0, 0, width, height);

  for (const p of particles) {
    p.y += p.speed * dt;
    p.rotation += p.rotationSpeed * dt;

    if (p.y - p.size > height) {
      p.y = -p.size;
      p.x = randomBetween(0, width);
    }

    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rotation);
    drawSlice(p.size);
    ctx.restore();
  }

  rafId = requestAnimationFrame(tick);
}

onMounted(() => {
  ctx = canvasRef.value.getContext("2d");
  readThemeColors();
  resizeCanvas();
  setParticleCount(sliceCountFor(quotesStore.total));
  window.addEventListener("resize", resizeCanvas);
  rafId = requestAnimationFrame(tick);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeCanvas);
  if (rafId) cancelAnimationFrame(rafId);
});

watch(
  () => quotesStore.total,
  (total) => setParticleCount(sliceCountFor(total))
);
</script>

<style scoped>
.melon-rain {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}
</style>